import type { AxisMetric, RiskItem } from "../QuadrantDashboard";


export type OrgCoverageStatus = "COVERED" | "UNMEASURED" | "EMPTY";

export interface FailureCycleBuckets {
  newly: number;
  two: number;
  three: number;
  fourPlus: number;
}

export interface OrgQuadrantRow {
  orgId: string;
  orgName: string;
  orgCode?: string | null;
  failedControlCount: number;
  continuousFailedControlCount: number;
  applicationCount: number;
  totalApplicationCount: number;
  applicationsFailing: number;
  assetCount: number;
  assetsFailing: number;
  automatedCycleCount: number;
  manualCycleCount: number;
  coverageStatus: OrgCoverageStatus;
  buckets?: FailureCycleBuckets;
  zone?: string;
}

export interface OrgQuadrantData {
  sourceType: string;
  groupId?: string | null;
  groupName?: string | null;
  range: string;
  baselineDay?: string;
  xThreshold: number;
  yThreshold: number;
  xMax: number;
  yMax: number;
  organizationCount: number;
  cleanOrgCount: number;
  organizations: OrgQuadrantRow[];
}

export interface OrgQuadrantSummaryData {
  sourceType: string;
  groupId?: string | null;
  groupName?: string | null;
  totalOrganizations: number;
  totalApplications: number;
  applicationsWithSsp: number;
  applicationsWithoutSsp: number;
  sspCoveragePercentage: number;
  openFindings: number;
  criticalFindings: number;
  criticalOpenFindings: number;
  totalAssets: number;
}

export interface OrgControlGroupRow {
  family: string;
  groupName?: string | null;
  failedControlCount: number;
  continuousFailedControlCount: number;
}

export interface OrgFailingControlRow {
  controlId: string;
  controlTitle?: string | null;
  family: string;
  groupName?: string | null;
  cyclesFailed: number;
  state: "NEW" | "CONTINUOUS" | string;
  source: "MANUAL" | "AUTOMATED" | "BOTH" | string;
  failedApplicationCount: number;
  failedAssetCount: number;
  affectedAssetTypes: string[];
}

export interface OrgApplicationRow {
  applicationId: string;
  applicationName: string;
  failedControlCount: number;
  continuousFailedControlCount: number;
}
export interface AssetTypeFailureRow {
  componentType: string;
  failedAssetCount: number;
  failedControlCount: number;
  buckets?: FailureCycleBuckets;
}
export interface OrgDrillDownData {
  orgId: string;
  orgName: string;
  orgCode?: string | null;
  range: string;
  baselineDay?: string;
  applicationCount: number;
  applicationsFailing: number;
  assetCount: number;
  assetsFailing: number;
  failedControlCount: number;
  continuousFailedControlCount: number;
  automatedCycleCount: number;
  manualCycleCount: number;
  buckets?: FailureCycleBuckets;
  controlGroups: OrgControlGroupRow[];
  controls: OrgFailingControlRow[];
  applications: OrgApplicationRow[];
  byAssetType: AssetTypeFailureRow[];
}
export interface ScopeItem {
  id: string;
  name: string;
}
export type OrgLevel =
  | "GROUP"
  | "ORGANIZATION"
  | "DIVISION"
  | "DEPARTMENT"
  | "APPLICATION";
export interface FilterScopeData {
  level: OrgLevel | null;
  items: ScopeItem[];
}
export interface QuadrantScopeOption {
  id: string;
  name: string;
  orgType: OrgLevel;
  parentId?: string | null;
  parentName?: string | null;
  applicationCount: number;
}
export interface QuadrantScopeOptionsData {
  level: OrgLevel | null;
  groups: QuadrantScopeOption[];
  organizations: QuadrantScopeOption[];
}
export interface QuadrantScopeSelection {
  groupIds: string[];
  orgIds: string[];
}
export const EMPTY_SCOPE: QuadrantScopeSelection = { groupIds: [], orgIds: [] };
export function scopeQuery(scope: QuadrantScopeSelection): string {
  const parts: string[] = [];
  /* Org and group are mutually exclusive, and the narrower one wins. A caller
     who holds a single group has it auto-seeded into the scope, so sending both
     would hand the backend a group that widens the org selection straight back
     out, so the filter would appear to do nothing. Matches `orgFilter` in
     ExecutiveInsights and `auditorScopeQuery` in frameworksApi. */
  if (scope.orgIds.length > 0) {
    parts.push(`org_ids=${scope.orgIds.join(",")}`);
  } else if (scope.groupIds.length > 0) {
    parts.push(`group_ids=${scope.groupIds.join(",")}`);
  }
  return parts.length === 0 ? "" : `&${parts.join("&")}`;
}
export function scopeQueryFirst(scope: QuadrantScopeSelection): string {
  const query = scopeQuery(scope);
  return query === "" ? "" : `?${query.slice(1)}`;
}
export function isScopeEmpty(scope: QuadrantScopeSelection): boolean {
  return scope.groupIds.length === 0 && scope.orgIds.length === 0;
}
export const ORG_COVERAGE_META: Record<
  OrgCoverageStatus,
  { label: string; hint: string; color: string }
> = {
  COVERED: {
    label: "Covered",
    hint: "At least one application has a published SSP",
    color: "#10b981",
  },
  UNMEASURED: {
    label: "Not Assessed",
    hint: "Has applications, none with a published SSP — nothing was evaluated",
    color: "#f59e0b",
  },
  EMPTY: {
    label: "No Applications",
    hint: "No applications in this organization yet",
    color: "#94a3b8",
  },
};
export function orgCoverageMeta(status?: OrgCoverageStatus) {
  return ORG_COVERAGE_META[status ?? "EMPTY"] ?? ORG_COVERAGE_META.EMPTY;
}

const whole = (v: number) => String(Math.round(v));

export const ORG_AXIS_METRICS: AxisMetric[] = [
  {
    id: "failedControls",
    label: "Failed Controls",
    axisTitle: "Failed Controls",
    getValue: (i) => i.riskCount,
    format: whole,
    isCategorical: false,
    defaultThreshold: 10,
  },
  {
    id: "repeatFailures",
    label: "Repeat Failures",
    axisTitle: "Controls Failing 2+ Cycles",
    getValue: (i) => i.continuousFailedControlCount ?? 0,
    format: whole,
    isCategorical: false,
    defaultThreshold: 5,
  },
  {
    id: "applications",
    label: "Applications",
    axisTitle: "Applications",
    getValue: (i) => i.totalApplicationCount ?? 0,
    format: whole,
    isCategorical: false,
    defaultThreshold: 10,
  },
  {
    id: "applicationsFailing",
    label: "Applications Failing",
    axisTitle: "Applications Failing",
    getValue: (i) => i.applicationsFailing ?? 0,
    format: whole,
    isCategorical: false,
    defaultThreshold: 5,
  },
  {
    id: "totalAssets",
    label: "Total Assets",
    axisTitle: "Total Assets",
    getValue: (i) => i.totalAssets ?? 0,
    format: whole,
    isCategorical: false,
    defaultThreshold: 50,
  },
  {
    id: "assetsFailing",
    label: "Assets Failing",
    axisTitle: "Assets Failing",
    getValue: (i) => i.assetsFailing ?? 0,
    format: whole,
    isCategorical: false,
    defaultThreshold: 20,
  },
];
export function normalizeOrgRow(row: OrgQuadrantRow): RiskItem {
  const totalApplications = row.totalApplicationCount ?? 0;
  return {
    id: row.orgId,
    name: row.orgName || "Organization",
    appCode: row.orgCode || undefined,
    entityType: "Organization",
    riskCount: row.failedControlCount ?? 0,
    driftCount: 0,
    hasChildren: totalApplications > 0,
    totalAssets: row.assetCount ?? 0,
    assetsWithElkData: 0,
    elkDataAvailable: (row.automatedCycleCount ?? 0) > 0,
    systemSensitivityLevel: "none",
    sizeValue: totalApplications,
    continuousFailedControlCount: row.continuousFailedControlCount ?? 0,
    applicationCount: row.applicationCount ?? 0,
    totalApplicationCount: totalApplications,
    applicationsFailing: row.applicationsFailing ?? 0,
    assetsFailing: row.assetsFailing ?? 0,
    coverageStatus: row.coverageStatus ?? "EMPTY",
  };
}